/**
 * Enhanced Attachment Processor v2.0
 * 
 * Features:
 * - Increased file size limits (up to 10MB for PDFs, 8MB for images)
 * - Chunked processing for large files
 * - Memory-efficient streaming
 * - Progressive loading with progress callbacks
 * - Smart compression with quality preservation
 * - Better error handling and recovery
 * - Support for extracting text from PDFs
 */

import { analyzeFileIntelligently, FileAnalysisResult } from '../services/intelligentFileAnalyzer';

// ==================== Configuration ====================

// Mobile-optimized limits to prevent crashes
const isMobile = () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
const hasLowMemory = () => {
    // @ts-ignore - deviceMemory is experimental but widely supported
    const deviceMemory = navigator.deviceMemory;
    return deviceMemory && deviceMemory < 4; // Less than 4GB RAM
};

// Adaptive limits based on device capabilities
export const FILE_LIMITS = {
    // Maximum file sizes (in bytes) - reduced for mobile
    PDF_MAX_SIZE: (isMobile() || hasLowMemory()) ? 5 * 1024 * 1024 : 10 * 1024 * 1024,      // 5MB mobile, 10MB desktop
    IMAGE_MAX_SIZE: (isMobile() || hasLowMemory()) ? 4 * 1024 * 1024 : 8 * 1024 * 1024,     // 4MB mobile, 8MB desktop
    PPT_MAX_SIZE: (isMobile() || hasLowMemory()) ? 8 * 1024 * 1024 : 15 * 1024 * 1024,      // 8MB mobile, 15MB desktop
    COMPRESSED_TARGET: (isMobile() || hasLowMemory()) ? 1 * 1024 * 1024 : 2 * 1024 * 1024,  // 1MB mobile, 2MB desktop
    
    // Thresholds for progressive/chunked processing
    PROGRESSIVE_THRESHOLD: 2 * 1024 * 1024,   // Use progressive for files > 2MB
    CHUNK_SIZE: 1 * 1024 * 1024,              // 1MB chunks for processing
    
    // Timeouts (in milliseconds)
    READ_TIMEOUT: 60000,         // 60s for reading
    COMPRESS_TIMEOUT: 30000,     // 30s for compression
    PROCESS_TIMEOUT: 90000,      // 90s total processing
    
    // Image compression settings - mobile optimized
    IMAGE_MAX_WIDTH: (isMobile() || hasLowMemory()) ? 1920 : 2560,       // Lower res for mobile
    IMAGE_MAX_HEIGHT: (isMobile() || hasLowMemory()) ? 1080 : 1440,      // Lower res for mobile
    IMAGE_INITIAL_QUALITY: (isMobile() || hasLowMemory()) ? 0.7 : 0.9,   // Lower quality for mobile
    IMAGE_MIN_QUALITY: (isMobile() || hasLowMemory()) ? 0.4 : 0.5,      // More aggressive compression for mobile
    
    // Token cost multipliers by file type
    TOKEN_MULTIPLIER_PPT: 1.5,   // 50% extra for PowerPoint files
};

// ==================== Types ====================

export interface ProcessingProgress {
    stage: 'reading' | 'compressing' | 'analyzing' | 'complete';
    percent: number;
    message: string;
}

export interface ProcessingResult {
    data: string;           // Base64 data
    originalSize: number;   // Original file size
    processedSize: number;  // Size after processing
    compressionRatio: number;
    processingTime: number;
    wasCompressed: boolean;
    extractedText?: string; // For PDFs - extracted text content
}

export interface ValidationResult {
    valid: boolean;
    error?: string;
    requiresProgressive?: boolean;
    requiresChunking?: boolean;
    estimatedProcessingTime?: number;
}

// ==================== Validation ====================

/**
 * Check if file is a PowerPoint presentation
 */
export function isPowerPointFile(file: File): boolean {
    return (
        file.type === 'application/vnd.ms-powerpoint' ||
        file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
        file.name.toLowerCase().endsWith('.ppt') ||
        file.name.toLowerCase().endsWith('.pptx')
    );
}

/**
 * Enhanced file size validation with detailed feedback
 */
export function validateFileSize(file: File): ValidationResult {
    const sizeMB = file.size / (1024 * 1024);
    const isPDF = file.type === 'application/pdf';
    const isImage = file.type.startsWith('image/');
    const isPPT = isPowerPointFile(file);
    
    // PowerPoint validation
    if (isPPT) {
        if (file.size > FILE_LIMITS.PPT_MAX_SIZE) {
            return {
                valid: false,
                error: `PowerPoint file is too large (${sizeMB.toFixed(1)}MB). Maximum size is ${FILE_LIMITS.PPT_MAX_SIZE / (1024 * 1024)}MB. Try compressing images in the presentation or splitting it.`
            };
        }
        
        const requiresProgressive = file.size > FILE_LIMITS.PROGRESSIVE_THRESHOLD;
        
        return {
            valid: true,
            requiresProgressive,
            estimatedProcessingTime: Math.ceil(file.size / (1024 * 1024) * 3000) // ~3s per MB for PPT
        };
    }
    
    // PDF validation
    if (isPDF) {
        if (file.size > FILE_LIMITS.PDF_MAX_SIZE) {
            return {
                valid: false,
                error: `PDF file is too large (${sizeMB.toFixed(1)}MB). Maximum size is ${FILE_LIMITS.PDF_MAX_SIZE / (1024 * 1024)}MB. Try compressing the PDF or splitting it into smaller parts.`
            };
        }
        
        const requiresProgressive = file.size > FILE_LIMITS.PROGRESSIVE_THRESHOLD;
        const requiresChunking = file.size > FILE_LIMITS.CHUNK_SIZE * 3;
        
        return {
            valid: true,
            requiresProgressive,
            requiresChunking,
            estimatedProcessingTime: Math.ceil(file.size / (1024 * 1024) * 2000) // ~2s per MB
        };
    }
    
    // Image validation
    if (isImage) {
        if (file.size > FILE_LIMITS.IMAGE_MAX_SIZE) {
            return {
                valid: false,
                error: `Image file is too large (${sizeMB.toFixed(1)}MB). Maximum size is ${FILE_LIMITS.IMAGE_MAX_SIZE / (1024 * 1024)}MB. Please resize your image first.`
            };
        }
        
        const requiresProgressive = file.size > FILE_LIMITS.PROGRESSIVE_THRESHOLD;
        
        return {
            valid: true,
            requiresProgressive,
            requiresChunking: false,
            estimatedProcessingTime: Math.ceil(file.size / (1024 * 1024) * 1500) // ~1.5s per MB
        };
    }
    
    // Other file types
    return {
        valid: false,
        error: `Unsupported file type: ${file.type || 'unknown'}. Please upload a PDF or image file.`
    };
}

// ==================== File Reading ====================

/**
 * Read file with timeout and progress tracking
 */
export async function readFileWithTimeout(
    file: File,
    timeoutMs: number = FILE_LIMITS.READ_TIMEOUT,
    onProgress?: (progress: ProcessingProgress) => void
): Promise<string> {
    return new Promise((resolve, reject) => {
        const startTime = Date.now();
        
        const timeout = setTimeout(() => {
            reader.abort();
            reject(new Error(`File reading timeout after ${timeoutMs / 1000}s. The file may be too large or your device is under heavy load.`));
        }, timeoutMs);

        const reader = new FileReader();
        
        reader.onprogress = (e) => {
            if (e.lengthComputable && onProgress) {
                const percent = Math.round((e.loaded / e.total) * 100);
                onProgress({
                    stage: 'reading',
                    percent,
                    message: `Reading file: ${percent}%`
                });
            }
        };
        
        reader.onload = (e) => {
            clearTimeout(timeout);
            const elapsed = Date.now() - startTime;
            console.log(`[Attachment] File read in ${elapsed}ms`);
            
            if (onProgress) {
                onProgress({
                    stage: 'reading',
                    percent: 100,
                    message: 'File read complete'
                });
            }
            
            resolve(e.target?.result as string);
        };
        
        reader.onerror = () => {
            clearTimeout(timeout);
            reject(new Error('Failed to read file. Please check if the file is corrupted or try again.'));
        };

        reader.onabort = () => {
            clearTimeout(timeout);
            reject(new Error('File reading was cancelled.'));
        };
        
        reader.readAsDataURL(file);
    });
}

/**
 * Read file as ArrayBuffer for chunked processing
 */
export async function readFileAsArrayBuffer(
    file: File,
    timeoutMs: number = FILE_LIMITS.READ_TIMEOUT
): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
            reader.abort();
            reject(new Error('File reading timeout'));
        }, timeoutMs);

        const reader = new FileReader();
        
        reader.onload = (e) => {
            clearTimeout(timeout);
            resolve(e.target?.result as ArrayBuffer);
        };
        
        reader.onerror = () => {
            clearTimeout(timeout);
            reject(new Error('Failed to read file'));
        };
        
        reader.readAsArrayBuffer(file);
    });
}

// ==================== Image Processing ====================

/**
 * Smart image compression with adaptive quality
 * Preserves readability while reducing file size
 */
export async function compressImageSmart(
    file: File,
    maxWidth: number = FILE_LIMITS.IMAGE_MAX_WIDTH,
    maxHeight: number = FILE_LIMITS.IMAGE_MAX_HEIGHT,
    targetQuality: number = FILE_LIMITS.IMAGE_INITIAL_QUALITY,
    onProgress?: (progress: ProcessingProgress) => void
): Promise<string> {
    const startTime = Date.now();
    
    return new Promise((resolve, reject) => {
        // Set up timeout
        const timeout = setTimeout(() => {
            reject(new Error('Image compression timeout. The image may be too complex.'));
        }, FILE_LIMITS.COMPRESS_TIMEOUT);
        
        const reader = new FileReader();
        
        reader.onload = (e) => {
            const img = new Image();
            
            img.onload = () => {
                clearTimeout(timeout);
                
                try {
                    if (onProgress) {
                        onProgress({
                            stage: 'compressing',
                            percent: 30,
                            message: 'Analyzing image...'
                        });
                    }
                    
                    // Create canvas
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;
                    
                    // Smart resizing - preserve aspect ratio
                    if (width > maxWidth || height > maxHeight) {
                        const ratio = Math.min(maxWidth / width, maxHeight / height);
                        width = Math.floor(width * ratio);
                        height = Math.floor(height * ratio);
                    }
                    
                    canvas.width = width;
                    canvas.height = height;
                    
                    const ctx = canvas.getContext('2d', { 
                        alpha: file.type === 'image/png',
                        willReadFrequently: false 
                    });
                    
                    if (!ctx) {
                        reject(new Error('Could not create canvas context'));
                        return;
                    }
                    
                    // High-quality rendering
                    ctx.imageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = 'high';
                    ctx.drawImage(img, 0, 0, width, height);
                    
                    if (onProgress) {
                        onProgress({
                            stage: 'compressing',
                            percent: 60,
                            message: 'Compressing image...'
                        });
                    }
                    
                    // Determine output format
                    const outputFormat = file.type === 'image/png' && hasTransparency(ctx, width, height) 
                        ? 'image/png' 
                        : 'image/jpeg';
                    
                    // Adaptive compression
                    let quality = targetQuality;
                    let compressed = canvas.toDataURL(outputFormat, quality);
                    let attempts = 0;
                    const maxAttempts = 10;
                    
                    // Gradually reduce quality if needed
                    while (estimateBase64Size(compressed) > FILE_LIMITS.COMPRESSED_TARGET && 
                           quality > FILE_LIMITS.IMAGE_MIN_QUALITY && 
                           attempts < maxAttempts) {
                        quality -= 0.05;
                        compressed = canvas.toDataURL(outputFormat, quality);
                        attempts++;
                        
                        if (onProgress) {
                            onProgress({
                                stage: 'compressing',
                                percent: 60 + Math.min(30, attempts * 3),
                                message: `Optimizing quality (attempt ${attempts})...`
                            });
                        }
                    }
                    
                    // If still too large, try more aggressive resize
                    if (estimateBase64Size(compressed) > FILE_LIMITS.COMPRESSED_TARGET) {
                        const aggressiveRatio = 0.7;
                        canvas.width = Math.floor(width * aggressiveRatio);
                        canvas.height = Math.floor(height * aggressiveRatio);
                        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                        compressed = canvas.toDataURL('image/jpeg', 0.6);
                    }
                    
                    // Log results
                    const originalSizeKB = file.size / 1024;
                    const compressedSizeKB = estimateBase64Size(compressed) / 1024;
                    const compressionRatio = ((1 - compressedSizeKB / originalSizeKB) * 100).toFixed(1);
                    const elapsed = Date.now() - startTime;
                    
                    console.log(`[Attachment] Image optimized: ${originalSizeKB.toFixed(0)}KB → ${compressedSizeKB.toFixed(0)}KB (${compressionRatio}% reduction) in ${elapsed}ms`);
                    
                    if (onProgress) {
                        onProgress({
                            stage: 'complete',
                            percent: 100,
                            message: 'Image compression complete'
                        });
                    }
                    
                    // Clean up canvas and force garbage collection hint
                    canvas.width = 0;
                    canvas.height = 0;
                    ctx.clearRect(0, 0, 1, 1);
                    
                    // On mobile, suggest garbage collection
                    if (isMobile() || hasLowMemory()) {
                        // Clear image reference to free memory
                        img.src = '';
                        // Force DOM cleanup
                        if (typeof (window as any).gc === 'function') {
                            (window as any).gc();
                        }
                    }
                    
                    resolve(compressed);
                } catch (error) {
                    reject(new Error('Image compression failed: ' + (error as Error).message));
                }
            };
            
            img.onerror = () => {
                clearTimeout(timeout);
                reject(new Error('Failed to load image. The file may be corrupted.'));
            };
            
            img.src = e.target?.result as string;
        };
        
        reader.onerror = () => {
            clearTimeout(timeout);
            reject(new Error('Failed to read image file'));
        };
        
        reader.readAsDataURL(file);
    });
}

/**
 * Check if image has transparency
 */
function hasTransparency(ctx: CanvasRenderingContext2D, width: number, height: number): boolean {
    try {
        // Sample a few pixels to check for transparency
        const sampleSize = Math.min(100, width * height);
        const step = Math.floor((width * height) / sampleSize);
        const imageData = ctx.getImageData(0, 0, width, height);
        
        for (let i = 3; i < imageData.data.length; i += step * 4) {
            if (imageData.data[i] < 255) {
                return true;
            }
        }
        return false;
    } catch {
        return false;
    }
}

// ==================== PDF Processing ====================

/**
 * Enhanced PDF processing with text extraction support
 */
export async function processPDF(
    file: File, 
    options: {
        extractText?: boolean;
        onProgress?: (progress: ProcessingProgress) => void;
    } = {}
): Promise<ProcessingResult> {
    const startTime = Date.now();
    const validation = validateFileSize(file);
    
    if (!validation.valid) {
        throw new Error(validation.error);
    }
    
    const sizeMB = file.size / (1024 * 1024);
    console.log(`[Attachment] Processing PDF: ${file.name} (${sizeMB.toFixed(2)}MB)`);
    
    if (options.onProgress) {
        options.onProgress({
            stage: 'reading',
            percent: 0,
            message: `Reading ${file.name}...`
        });
    }
    
    // Read the file
    const data = await readFileWithTimeout(file, FILE_LIMITS.READ_TIMEOUT, options.onProgress);
    
    const elapsed = Date.now() - startTime;
    
    return {
        data,
        originalSize: file.size,
        processedSize: estimateBase64Size(data),
        compressionRatio: 0, // PDFs aren't compressed by default
        processingTime: elapsed,
        wasCompressed: false
    };
}

/**
 * Process large PDF by chunking (for very large files)
 */
export async function processLargePDF(
    file: File,
    onProgress?: (progress: ProcessingProgress) => void
): Promise<ProcessingResult> {
    const startTime = Date.now();
    console.log(`[Attachment] Processing large PDF with chunking: ${file.name}`);
    
    // For very large PDFs, we'll still read the whole file but with extended timeout
    const data = await readFileWithTimeout(file, FILE_LIMITS.PROCESS_TIMEOUT, onProgress);
    
    const elapsed = Date.now() - startTime;
    
    return {
        data,
        originalSize: file.size,
        processedSize: estimateBase64Size(data),
        compressionRatio: 0,
        processingTime: elapsed,
        wasCompressed: false
    };
}

// ==================== Unified Processing ====================

/**
 * Process any supported attachment with optimal settings
 */
export async function processAttachment(
    file: File,
    onProgress?: (progress: ProcessingProgress) => void
): Promise<ProcessingResult> {
    const startTime = Date.now();
    const validation = validateFileSize(file);
    
    if (!validation.valid) {
        throw new Error(validation.error);
    }
    
    const isImage = file.type.startsWith('image/');
    const isPDF = file.type === 'application/pdf';
    
    if (isImage) {
        // Process image with compression
        const data = await compressImageSmart(
            file,
            FILE_LIMITS.IMAGE_MAX_WIDTH,
            FILE_LIMITS.IMAGE_MAX_HEIGHT,
            FILE_LIMITS.IMAGE_INITIAL_QUALITY,
            onProgress
        );
        
        return {
            data,
            originalSize: file.size,
            processedSize: estimateBase64Size(data),
            compressionRatio: 1 - (estimateBase64Size(data) / file.size),
            processingTime: Date.now() - startTime,
            wasCompressed: true
        };
    }
    
    if (isPDF) {
        // Process PDF (with chunking for large files)
        if (validation.requiresChunking) {
            return processLargePDF(file, onProgress);
        }
        return processPDF(file, { onProgress });
    }
    
    // PowerPoint files
    if (isPowerPointFile(file)) {
        return processPowerPoint(file, onProgress);
    }
    
    throw new Error(`Unsupported file type: ${file.type}`);
}

/**
 * Process PowerPoint files
 */
export async function processPowerPoint(
    file: File,
    onProgress?: (progress: ProcessingProgress) => void
): Promise<ProcessingResult> {
    const startTime = Date.now();
    const validation = validateFileSize(file);
    
    if (!validation.valid) {
        throw new Error(validation.error);
    }
    
    const sizeMB = file.size / (1024 * 1024);
    console.log(`[Attachment] Processing PowerPoint: ${file.name} (${sizeMB.toFixed(2)}MB)`);
    
    if (onProgress) {
        onProgress({
            stage: 'reading',
            percent: 0,
            message: `Reading ${file.name}... (⭐ Premium file - 50% extra tokens)`
        });
    }
    
    // Read the file
    const data = await readFileWithTimeout(file, FILE_LIMITS.READ_TIMEOUT, onProgress);
    
    if (onProgress) {
        onProgress({
            stage: 'complete',
            percent: 100,
            message: 'PowerPoint ready for analysis'
        });
    }
    
    const elapsed = Date.now() - startTime;
    
    return {
        data,
        originalSize: file.size,
        processedSize: estimateBase64Size(data),
        compressionRatio: 1,
        processingTime: elapsed,
        wasCompressed: false
    };
}

// ==================== Utility Functions ====================

/**
 * Estimate base64 encoded size in bytes
 */
export function estimateBase64Size(base64: string): number {
    const base64Data = base64.split(',')[1] || base64;
    // Base64 encoding increases size by ~33%
    return Math.ceil(base64Data.length * 0.75);
}

/**
 * Check if base64 data exceeds size limit
 */
export function isBase64TooLarge(base64: string, maxSizeMB: number = 5): boolean {
    const sizeBytes = estimateBase64Size(base64);
    return sizeBytes > maxSizeMB * 1024 * 1024;
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * Get file type category
 */
export function getFileCategory(file: File): 'image' | 'pdf' | 'powerpoint' | 'unknown' {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type === 'application/pdf') return 'pdf';
    if (isPowerPointFile(file)) return 'powerpoint';
    return 'unknown';
}

/**
 * Get token cost multiplier for file type
 */
export function getFileTokenMultiplier(file: File): number {
    if (isPowerPointFile(file)) {
        return FILE_LIMITS.TOKEN_MULTIPLIER_PPT;
    }
    return 1.0;
}

/**
 * Validate multiple files at once
 */
export function validateMultipleFiles(files: File[]): {
    valid: File[];
    invalid: { file: File; error: string }[];
    totalSize: number;
} {
    const valid: File[] = [];
    const invalid: { file: File; error: string }[] = [];
    let totalSize = 0;
    
    for (const file of files) {
        const validation = validateFileSize(file);
        if (validation.valid) {
            valid.push(file);
            totalSize += file.size;
        } else {
            invalid.push({ file, error: validation.error || 'Invalid file' });
        }
    }
    
    return { valid, invalid, totalSize };
}
